/**
 * Serviço de Autenticação - JurisPrazo
 * Gerencia login, logout e estado de autenticação
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { FirebaseService } from './firebaseService';

export class AuthService {
  
  /**
   * Fazer login com email e senha
   */
  static async signInWithEmail(email, password) {
    try {
      console.log('🔐 Fazendo login com email:', email);
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log('✅ Login realizado com sucesso:', user.uid);
      
      // Atualizar último login
      await FirebaseService.updateUserProfile(user.uid, {
        lastLogin: new Date().toISOString()
      });
      
      return user;
    } catch (error) {
      console.error('❌ Erro no login:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Criar conta com email e senha
   */
  static async createAccount(email, password, displayName) {
    try {
      console.log('👤 Criando conta para:', email);
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Atualizar perfil com nome
      if (displayName) {
        await updateProfile(user, { displayName });
      }
      
      // Criar perfil no Firestore
      await FirebaseService.createUserProfile(user.uid, {
        email: user.email,
        displayName: displayName || user.displayName,
        createdAt: new Date().toISOString(),
        onboardingCompleted: false
      });
      
      console.log('✅ Conta criada com sucesso:', user.uid);
      return user;
    } catch (error) {
      console.error('❌ Erro ao criar conta:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Login com Google
   */
  static async signInWithGoogle() {
    try {
      console.log('🔐 Fazendo login com Google...');
      
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Verificar se é primeira vez do usuário
      const userProfile = await FirebaseService.getUserProfile(user.uid);
      
      if (!userProfile) {
        // Criar perfil para novo usuário
        await FirebaseService.createUserProfile(user.uid, {
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          createdAt: new Date().toISOString(),
          onboardingCompleted: false,
          provider: 'google'
        });
      } else {
        // Atualizar último login
        await FirebaseService.updateUserProfile(user.uid, {
          lastLogin: new Date().toISOString()
        });
      }
      
      console.log('✅ Login com Google realizado:', user.uid);
      return user;
    } catch (error) {
      console.error('❌ Erro no login com Google:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Fazer logout
   */
  static async signOut() {
    try {
      console.log('🚪 Fazendo logout...');
      await signOut(auth);
      console.log('✅ Logout realizado com sucesso');
    } catch (error) {
      console.error('❌ Erro no logout:', error);
      throw error;
    }
  }

  /**
   * Resetar senha
   */
  static async resetPassword(email) {
    try {
      console.log('🔑 Enviando email de reset para:', email);
      await sendPasswordResetEmail(auth, email);
      console.log('✅ Email de reset enviado');
    } catch (error) {
      console.error('❌ Erro ao enviar email de reset:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Escutar mudanças no estado de autenticação
   */
  static onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('👤 Usuário autenticado:', user.uid);
        // Adicionar flag para identificar usuários demo
        user.isDemoUserManual = false;
        callback(user);
      } else {
        console.log('👤 Usuário não autenticado');
        callback(null);
      }
    });
  }

  /**
   * Obter usuário atual
   */
  static getCurrentUser() {
    return auth.currentUser;
  }

  /**
   * Verificar se usuário está autenticado
   */
  static isAuthenticated() {
    return !!auth.currentUser;
  }

  /**
   * Criar usuário demo (sem autenticação)
   */
  static createDemoUser() {
    console.log('🎭 Criando usuário demo...');
    
    const demoUser = {
      uid: 'demo-user-' + Date.now(),
      email: 'demo@jurisprazo.com',
      displayName: 'Usuário Demo',
      isDemoUserManual: true,
      photoURL: null,
      emailVerified: false
    };
    
    console.log('✅ Usuário demo criado:', demoUser.uid);
    return demoUser;
  }

  /**
   * Tratar erros de autenticação
   */
  static handleAuthError(error) {
    const errorMessages = {
      'auth/user-not-found': 'Usuário não encontrado. Verifique o email.',
      'auth/wrong-password': 'Senha incorreta. Tente novamente.',
      'auth/email-already-in-use': 'Este email já está em uso.',
      'auth/weak-password': 'A senha deve ter pelo menos 6 caracteres.',
      'auth/invalid-email': 'Email inválido.',
      'auth/user-disabled': 'Esta conta foi desabilitada.',
      'auth/too-many-requests': 'Muitas tentativas. Tente novamente mais tarde.',
      'auth/network-request-failed': 'Erro de conexão. Verifique sua internet.',
      'auth/popup-closed-by-user': 'Login cancelado pelo usuário.',
      'auth/cancelled-popup-request': 'Login cancelado.',
      'auth/popup-blocked': 'Popup bloqueado pelo navegador.'
    };

    const message = errorMessages[error.code] || error.message || 'Erro desconhecido';
    
    return new Error(message);
  }

  /**
   * Validar email
   */
  static validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validar senha
   */
  static validatePassword(password) {
    return password && password.length >= 6;
  }

  /**
   * Obter informações do usuário atual
   */
  static async getCurrentUserInfo() {
    const user = this.getCurrentUser();
    if (!user) return null;

    try {
      const userProfile = await FirebaseService.getUserProfile(user.uid);
      return {
        ...user,
        profile: userProfile
      };
    } catch (error) {
      console.error('❌ Erro ao obter informações do usuário:', error);
      return user;
    }
  }

  /**
   * Atualizar perfil do usuário
   */
  static async updateUserProfile(updates) {
    const user = this.getCurrentUser();
    if (!user) throw new Error('Usuário não autenticado');

    try {
      // Atualizar no Firebase Auth se necessário
      if (updates.displayName || updates.photoURL) {
        await updateProfile(user, {
          displayName: updates.displayName || user.displayName,
          photoURL: updates.photoURL || user.photoURL
        });
      }

      // Atualizar no Firestore
      await FirebaseService.updateUserProfile(user.uid, updates);
      
      console.log('✅ Perfil atualizado com sucesso');
    } catch (error) {
      console.error('❌ Erro ao atualizar perfil:', error);
      throw error;
    }
  }
} 